import {
  Box,
  Button,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState, useCallback, useRef } from "react";
import { useProposalGenerationStore } from "../store/ProposalGenerationStore";
import {
  useReportTemplateList,
  useUploadReportTemplate,
} from "src/views/reports/ReportServices";
import { ReportType } from "src/interfaces/enums";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "src/helpers/apiError";
import { Can } from "src/helpers/Auth";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

type TemplateCreatorProps = {
  onCancel: () => void;
  onSuccess: () => void;
};

const TemplateCreator = ({ onCancel, onSuccess }: TemplateCreatorProps) => {
  const { setSelectedTemplate } = useProposalGenerationStore();
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateDescription, setNewTemplateDescription] = useState("");
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { refetch } = useReportTemplateList(ReportType.GENERATIVE);

  const { mutateAsync: uploadTemplate, isLoading: isUploading } =
    useUploadReportTemplate();

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setTemplateFile(file);
      }
    },
    [],
  );

  const handleCreateTemplate = useCallback(async () => {
    if (!templateFile || !newTemplateName) return;

    try {
      // Create FormData matching ReportModals.jsx pattern
      const formData = new FormData();
      formData.append("template_binary", templateFile);
      formData.append("report_template_name", newTemplateName);
      formData.append("report_template_description", newTemplateDescription);

      // Use the existing uploadReportTemplate service
      const result = await uploadTemplate({
        reportType: ReportType.GENERATIVE,
        reportTemplate: {
          templateFile: formData,
          templateName: newTemplateName,
          templateDescription: newTemplateDescription,
          overwriteExistingFile: false,
        },
      });

      console.log("[TemplateCreator] Template created successfully:", result);

      NucToast({
        message: "Template created successfully",
        type: "success",
      });

      // Refetch templates to get the new template list
      const { data: updatedTemplates } = await refetch();

      // Auto-select the newly created template by finding it by name
      if (updatedTemplates) {
        const newTemplate = updatedTemplates.find(
          (t: any) => t.name === newTemplateName,
        );
        if (newTemplate) {
          console.log(
            "[TemplateCreator] Auto-selecting newly created template:",
            newTemplate,
          );
          setSelectedTemplate(newTemplate);
        }
      }

      onSuccess();
    } catch (err: any) {
      console.error("Error creating template:", err);
      NucToast({
        message: extractErrorDetail(err, "Failed to create template"),
        type: "error",
      });
    }
  }, [
    templateFile,
    newTemplateName,
    newTemplateDescription,
    uploadTemplate,
    refetch,
    onSuccess,
    setSelectedTemplate,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 200,
      }}
    >
      <Stack spacing={2}>
        <TextField
          label="Template Name"
          value={newTemplateName}
          onChange={(e) => setNewTemplateName(e.target.value)}
          fullWidth
          required
          size="small"
          data-attr="proposal-gen:input:template-name"
        />
        <TextField
          label="Description"
          value={newTemplateDescription}
          onChange={(e) => setNewTemplateDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
        />
        <Box>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".docx,.doc"
            onChange={handleFileSelect}
          />
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            fullWidth
            size="small"
            data-attr="proposal-gen:btn:upload-template-file"
          >
            {templateFile ? templateFile.name : "Upload Template File (.docx)"}
          </Button>
          {templateFile && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              File: {templateFile.name} ({(templateFile.size / 1024).toFixed(2)}{" "}
              KB)
            </Typography>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 2 }}
        >
          <Button
            onClick={onCancel}
            disabled={isUploading}
            startIcon={<ArrowBackIcon />}
            size="small"
            data-attr="proposal-gen:btn:back-to-template-select"
          >
            Back to Selection
          </Button>
          <Can
            perform="report:write"
            yes={() => (
              <Button
                onClick={handleCreateTemplate}
                variant="contained"
                disabled={!templateFile || !newTemplateName || isUploading}
                size="small"
                data-attr="proposal-gen:btn:create-template"
              >
                {isUploading ? "Creating..." : "Create Template"}
              </Button>
            )}
            no={() => (
              <Tooltip title="User doesn't have permissions report:write">
                <span>
                  <Button
                    variant="contained"
                    disabled
                    size="small"
                    data-attr="proposal-gen:btn:create-template"
                  >
                    Create Template
                  </Button>
                </span>
              </Tooltip>
            )}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default TemplateCreator;
